import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ErrorService } from 'src/app/services/error.service';
import { RoleService } from 'src/app/services/role.service';
import { environment } from 'src/environments/environment';
import * as RoleActions from '../actions/role.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class RoleEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private roleService: RoleService,
    private router: Router,
    private errorService: ErrorService
  ) {}

  createRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.createRole),
      switchMap(({ payload }) => {
        this.store.dispatch(
          RoleActions.roleLoading({ payload: { loading: true } })
        );
        return this.roleService.createRole(payload.role).pipe(
          map((response) => ({ payload, response })),
          mergeMap(() => {
            this.store.dispatch(RoleActions.roleCreated());
            return of(RoleActions.roleLoading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            return of(
              RoleActions.roleActionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.readRoles),
      switchMap(({ payload }) => {
        this.store.dispatch(
          RoleActions.roleLoading({ payload: { loading: true } })
        );
        return this.roleService.readRoles(payload).pipe(
          mergeMap((value) => {
            this.store.dispatch(
              RoleActions.rolesRead({ payload: { roles: value.data } })
            );
            return of(RoleActions.roleLoading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            return of(
              RoleActions.roleActionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getRoleById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.readRoleById),
      switchMap(({ payload }) => {
        return this.roleService.readRoleById(payload.role_id).pipe(
          mergeMap((value) => {
            return of(
              RoleActions.roleByIdRead({ payload: { role: value.data } })
            );
          }),
          catchError((error) => {
            if (error instanceof Error)
              this.store.dispatch(RoleActions.redirectToRolesList());
            return of(
              RoleActions.roleActionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  redirectToRolesList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RoleActions.redirectToRolesList),
        tap(async () => {
          await this.router.navigate(['/security/roles']);
        })
      ),
    { dispatch: false }
  );

  updateRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.updateRole),
      switchMap(({ payload }) => {
        this.store.dispatch(
          RoleActions.roleLoading({ payload: { loading: true } })
        );
        return this.roleService.updateRole(payload.role).pipe(
          map((response) => ({ payload, response })),
          mergeMap(() => {
            this.store.dispatch(RoleActions.roleUpdated());
            return of(RoleActions.roleLoading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            if (environment.optimisticLockErrors.includes(error.error?.name))
              return of(
                RoleActions.roleOptimisticLockError({
                  payload: {
                    optimisticLockError: this.errorService.mapError(error),
                  },
                })
              );
            else
              return of(
                RoleActions.roleActionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
          })
        );
      })
    )
  );

  deleteRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.deleteRole),
      switchMap(({ payload }) => {
        this.store.dispatch(
          RoleActions.roleLoading({ payload: { loading: true } })
        );
        return this.roleService
          .deleteRole(payload.role_id, payload.version)
          .pipe(
            mergeMap(() => {
              this.store.dispatch(RoleActions.roleDeleted());
              return of(
                RoleActions.roleLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              if (environment.optimisticLockErrors.includes(error.error?.name))
                return of(
                  RoleActions.roleOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              else
                return of(
                  RoleActions.roleActionFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
            })
          );
      })
    )
  );

  getBuildInRolePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.roleReadBuildInRolePermissions),
      switchMap(({ payload }) => {
        this.store.dispatch(
          RoleActions.roleLoading({ payload: { loading: true } })
        );
        return this.roleService
          .readBuildInRolePermissions(payload.build_in_role)
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                RoleActions.roleBuildInRolePermissionsRead({
                  payload: {
                    permissions: response.data['permissions'],
                  },
                })
              );
              return of(
                RoleActions.roleLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              return of(
                RoleActions.roleActionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );
}
